import React, { useLayoutEffect, useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/common/hooks/useAuth';
import URL from '@/common/routes/Url';
import Avatar from '@mui/material/Avatar';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import Copyright from '@/components/common/Copyright';
import { useSnackbar } from 'notistack';
import { Link } from '@mui/material';
import HistoryService from '@/services/History.service';
import { History } from '@/common/constants/History';

import { Dialog, DialogActions, DialogContent } from '@mui/material';
import { Grid, Divider } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import CompanyService from '@/services/Company.service';


const theme = createTheme();

const SignIn: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const [agreeDigOpen, setAgreeDigOpen] = useState(false);
  const [navigateUrl, setNavigateUrl] = useState('');

  useLayoutEffect(() => {
    auth.signOut();
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = await auth.signIn(username, password);
    if (result !== false && result instanceof Object === true) {
      const redirectUrl = !result.departments.length ? URL.company : `/?departmentId=${result.departments[0].id}`;
      setNavigateUrl(redirectUrl);
      await HistoryService.create({ type: History['ログイン'] });
      let agreeDate = null; 
      await CompanyService.get().then((response: { data: any }) => {
        agreeDate = response.data.agreeDate;
      });
      if (agreeDate == null) {
        setAgreeDigOpen(true);
      } else {
        navigate(redirectUrl);
      }
    } else {
      enqueueSnackbar('認証に失敗しました。');
    }
  };



  /**
   * 利用規約ダイアログの生成
   * @returns 
   */
  const [agreeChecked, setAgreeChecked] = useState<boolean>(false);

  const handleAgreeCheckedChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (event.target.type == "checkbox") {
      setAgreeChecked(event.target.checked);

    }
  }

  const agreeDialog = () => {
    return (
      <div>
        <Dialog
          open={agreeDigOpen}>
          { }
          <DialogContent>
            <Box style={{ whiteSpace: 'pre-line', marginLeft: '0px', height: '50px', fontSize: '12pt' }}>
              <Link href="/manual/terms.pdf" target='_blank'>
                利用規約
              </Link>
              に同意してサインイン
            </Box>
            <Box style={{ whiteSpace: 'pre-line' }}>
              <Grid container spacing={1}>
                <Grid item style={{ display: 'flex' }}>
                  <div style={{ marginTop: '0px', marginLeft: '70px' }}>
                    <FormControlLabel control={
                      <Checkbox
                        name='mailChecked'
                        size='small'
                        checked={agreeChecked || false}
                        onChange={handleAgreeCheckedChange}
                      />
                    } label='同意する' />
                  </div>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>


          <DialogActions style={{ alignItems: 'center', justifyContent: 'center' }} >
            <Button onClick={() => agreeDialogOk()} disabled={!agreeChecked} >OK</Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  //利用規約 OK
  const agreeDialogOk = () => {
    //利用規約同意日付の更新
    try {
      CompanyService.agreeUpdate().then((response: { data: any }) => {
        navigate(navigateUrl);
        setAgreeDigOpen(false);
      }).catch(e => {
        console.log('agreeUpdate error.');
        enqueueSnackbar(e.response.data.message);
      });;
    } catch (error: any) {
      console.log('agreeUpdate error');
      enqueueSnackbar(error.response.data.message);
      return;
    }
  };



  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >            
          {agreeDialog()}
          <Avatar variant="rounded" src="/mitelink.svg" sx={{ width: 300, height: 91 }} />
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="メールアドレス"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="パスワード"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <LoadingButton type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} loading={auth.isLoading}>
              ログイン
            </LoadingButton>
          </Box>
          <div style={{ marginTop: "5px", textAlign: "center" }}>
            <Link href="/home/privacy">
              プライバシーポリシー
            </Link>
            及び
            <Link href="/manual/terms.pdf" target='_blank'>
              利用規約
            </Link>
            に同意してサインイン
          </div>
          <Link
            variant="body1"
            onClick={() => {
              navigate(`${URL.forgotPassword}`);
            }}
            style={{cursor: "pointer"}}
          >
            パスワード設定はこちらから
          </Link>
        </Box>
      </Container>
      <Stack
        direction={'row'}
        divider={<Divider orientation="vertical" flexItem />}
        sx={{ flexWrap: 'wrap', justifyContent: 'center', mt: '10px' }}
        >
          <Link href="/home" sx={{ m: '5px 10px' }}>ホーム</Link>
          <Link href="/home/overview" sx={{ m: '5px 10px' }}>会社概要</Link>
          <Link href="/home/sct" sx={{ m: '5px 10px' }}>特定商取引に基づく表記</Link>
          <Link href="/home/privacy" sx={{ m: '5px 10px' }}>プライバシーポリシー</Link>
          <Link href="/manual/terms.pdf" target='_blank' sx={{ m: '5px 10px' }}>利用規約</Link>
        </Stack>
        <Copyright/>
    </ThemeProvider>
  );
};

export default SignIn;
